import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Award, User } from "lucide-react";
import { timeAgo } from "../utilis/Time";

const memberDisplayName = (member = {}) =>
  member?.preferredName ||
  [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim() ||
  member?.memberId ||
  "Member";

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const item = items[index];
  if (!item) return null;
  const authorName = memberDisplayName(item.user);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl overflow-hidden max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl"
        >
          {/* Image */}
          {item.image && (
            <div className="relative">
              <img src={item.image} alt={item.title}
                className="w-full h-72 object-cover" />
              {/* Nav arrows */}
              {items.length > 1 && (
                <>
                  <button onClick={onPrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition">
                    <ChevronLeft size={18} className="text-gray-700" />
                  </button>
                  <button onClick={onNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition">
                    <ChevronRight size={18} className="text-gray-700" />
                  </button>
                </>
              )}
              {/* Close */}
              <button onClick={onClose}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition">
                <X size={16} className="text-gray-700" />
              </button>
              {/* Approved badge */}
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500 text-white text-xs font-semibold font-outfit">
                  <Award size={11} strokeWidth={2.5} /> Approved
                </span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 overflow-y-auto">
            <h3 className="font-cinzel text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
            {item.description && (
              <p className="font-outfit text-sm text-gray-600 leading-relaxed mb-4">{item.description}</p>
            )}
            {/* Author */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              {item.user?.pfp ? (
                <img src={item.user.pfp} alt={authorName}
                  className="w-9 h-9 rounded-full object-cover border border-gray-100" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-cyan-600" />
                </div>
              )}
              <div>
                <p className="font-outfit font-semibold text-gray-900 text-sm">
                  {authorName}
                </p>
                {item.member_bio && (
                  <p className="font-outfit text-xs text-gray-400">{item.member_bio}</p>
                )}
              </div>
              <span className="ml-auto font-outfit text-xs text-gray-400">{timeAgo(item.createdAt)}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CommunityShowcase = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [hasMore, setHasMore]         = useState(false);
  const [page, setPage]               = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const fetchApproved = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true); else setLoadingMore(true);
      const res = await axios.get(`/api/quest/approved?page=${pageNum}&limit=12`);
      if (res.data.success) {
        setSubmissions((prev) => append ? [...prev, ...res.data.quests] : res.data.quests);
        setHasMore(res.data.hasMore);
      }
    } catch (err) {
      console.error("Failed to load showcase:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchApproved(1); }, [fetchApproved]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchApproved(next, true);
  };

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevItem = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + submissions.length) % submissions.length); };
  const nextItem = (e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % submissions.length); };

  if (!loading && submissions.length === 0) return null;

  return (
    <motion.section
      className="main-lwbl bg-white py-16 px-4 rounded-[30px]"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7 }}
    >
      <div className="max-w-6xl mx-auto">

        {/* Heading */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold font-outfit mb-3">
            <Award size={12} strokeWidth={2.5} /> Community Approved
          </span>
          <h2 className="font-cinzel text-[32px] md:text-[40px] text-gray-900 mt-1 mb-3">
            Member Showcase
          </h2>
          <p className="font-outfit text-sm md:text-base text-gray-500 max-w-xl mx-auto">
            Celebrating the creative work of our community members. These submissions have been reviewed and approved by our team.
          </p>
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-100 animate-pulse aspect-square" />
            ))}
          </div>
        ) : (
          <>
            {/* Gallery grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {submissions.map((s, i) => (
                <motion.div
                  key={s._id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.08 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  onClick={() => openLightbox(i)}
                  className="relative rounded-2xl overflow-hidden cursor-pointer group border border-gray-100 shadow-sm aspect-square"
                >
                  {/* Image */}
                  {s.image ? (
                    <img src={s.image} alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cyan-50 to-blue-100 flex items-center justify-center">
                      <Award size={32} className="text-cyan-300" strokeWidth={1} />
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                    <p className="font-cinzel text-white text-sm font-bold leading-tight line-clamp-2">{s.title}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {s.user?.pfp ? (
                        <img src={s.user.pfp} alt={memberDisplayName(s.user)}
                          className="w-5 h-5 rounded-full object-cover border border-white/50" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                          <User size={10} className="text-white" />
                        </div>
                      )}
                      <span className="font-outfit text-white/80 text-xs">{memberDisplayName(s.user)}</span>
                    </div>
                  </div>

                  {/* Approved badge */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-semibold font-outfit">
                      <Award size={9} strokeWidth={2.5} /> Approved
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center mt-10">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-outfit font-semibold text-black shadow-md disabled:opacity-60"
                  style={{ background: "linear-gradient(29deg, rgba(0,240,251,1) 0%, rgba(255,255,255,1) 50%, rgba(0,240,251,1) 100%)" }}
                >
                  {loadingMore ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : "View More"}
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          items={submissions}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevItem}
          onNext={nextItem}
        />
      )}
    </motion.section>
  );
};

export default CommunityShowcase;
